import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import './App.css';

// Import page components
import {
  LoginPage,
  CallbackPage,
  HomePage,
  DashboardPage,
  BusinessAcumenPage,
  ScenarioSelectionPage,
  VoiceSessionPage,
  EvaluationViewPage,
  AdminEvaluationReviewPage,
  AdminUserManagementPage,
  AdminScenarioManagementPage,
} from './pages';

// Import authentication and layout components
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayoutWrapper } from './components/AppLayout';

// Apply Cloudscape light mode theme
applyMode(Mode.Light);

/**
 * Main App component for the GenAI Role Play Training Platform.
 * Uses Cloudscape design system for consistent AWS-style UI.
 *
 * Routes:
 * - /login - Login page (public, no layout wrapper)
 * - /home - Welcome page with program navigation
 * - /my-progress - User stats and session history
 * - /programs/business-acumen - Business Acumen program landing page
 * - /programs/business-acumen/scenarios - Scenario selection for BASA
 * - /session/:scenarioId - Voice session page
 * - /evaluation/:sessionId - Evaluation view page
 * - /admin/evaluation-reviews - Admin evaluation calibration review
 * - /admin/users - Admin user management (admin only)
 * - /admin/scenarios - Admin scenario management (admin only)
 *
 * Legacy routes (/dashboard, /scenarios) redirect to new paths.
 *
 * Authentication and Authorization:
 * - AuthProvider wraps the entire app for authentication state
 * - ProtectedRoute guards authenticated routes
 * - ProtectedRoute with requireAdmin guards admin routes
 * - AppLayoutWrapper provides navigation for authenticated pages
 *
 * @validates Requirements 13.1 - Redirects unauthenticated users to login
 * @validates Requirements 13.5 - Restricts admin pages to admin users only
 */
function App(): React.ReactElement {
  return (
    <AuthProvider>
      <Routes>
        {/* Public routes - Login page without AppLayoutWrapper */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />

        {/* Home - main landing page after login */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <HomePage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* My Progress - stats and session history (formerly Dashboard) */}
        <Route
          path="/my-progress"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <DashboardPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Business Acumen Program - landing page */}
        <Route
          path="/programs/business-acumen"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <BusinessAcumenPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Business Acumen - Scenario Selection */}
        <Route
          path="/programs/business-acumen/scenarios"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <ScenarioSelectionPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Voice session - active training session */}
        <Route
          path="/session/:scenarioId"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <VoiceSessionPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Evaluation view */}
        <Route
          path="/evaluation/:sessionId"
          element={
            <ProtectedRoute>
              <AppLayoutWrapper>
                <EvaluationViewPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Protected admin routes - require admin role */}
        <Route
          path="/admin/evaluation-reviews"
          element={
            <ProtectedRoute requireAdmin>
              <AppLayoutWrapper>
                <AdminEvaluationReviewPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AppLayoutWrapper>
                <AdminUserManagementPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/scenarios"
          element={
            <ProtectedRoute requireAdmin>
              <AppLayoutWrapper>
                <AdminScenarioManagementPage />
              </AppLayoutWrapper>
            </ProtectedRoute>
          }
        />

        {/* Legacy redirects - keep old bookmarks working */}
        <Route path="/dashboard" element={<Navigate to="/my-progress" replace />} />
        <Route path="/scenarios" element={<Navigate to="/programs/business-acumen/scenarios" replace />} />

        {/* Default redirect: root to home */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Catch-all: redirect unknown routes to home */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
